// ============================================
// Tag Handler — replaces Unity TagHandler.cs
// Parses Ink tags and routes to event bus
// ============================================

import { events, Events } from './events.js';
import { InkTags, SPEAKER_DISPLAY_NAMES } from './constants.js';
import { L } from '../systems/localization.js';

// Display names for compound #char: tags where split('_')[0] gives the wrong name
const COMPOUND_SPEAKER_NAMES = {
    'club_mamavee': 'Mama Vee',
    'club_manager': 'Mama Vee',
    'club_bartender': 'Bartender',
    'club_bouncer': 'Bouncer',
    'club_diego': 'Diego',
    'club_dj': 'DJ',
    'club_girl': 'Dancer',
    'club_lily': 'Lily',
    'club_marco': 'Marco',
    'club_stranger': 'Stranger',
    'club_vip_bouncer': 'VIP Bouncer',
    'club_vip_man': 'VIP',
    'salon_receptionist': 'Receptionist',
    'salon_stylist': 'Stylist',
    'dr_mercer': 'Dr. Mercer',
    'dr_mercer_warm': 'Dr. Mercer',
    'dr_mercer_concerned': 'Dr. Mercer',
    'dr_mercer_proud': 'Dr. Mercer',
    'clinic_receptionist': 'Receptionist',
    'clinic_nurse': 'Nurse',
    'salon_tech': 'Technician',
    'salon_artist': 'Makeup Artist',
    'salon_nail_tech': 'Nail Tech',
    'salon_piercer': 'Piercer',
    'clerk_adult': 'Veronica',
    'bar_customer': 'Customer',
    'bar_patron': 'Patron',
    'sugar_daddy': 'Richard',
    'sugar_baby': 'Sugar Baby',
    'saleswoman_clothing': 'Saleswoman',
    'dating_match': 'Match',
    'boss_dominant': 'Mr. Hartwell',
    'boss_intimate': 'Mr. Hartwell',
    'boss_neutral': 'Mr. Hartwell',
    'boss_pleased': 'Mr. Hartwell',
    'boss_stern': 'Mr. Hartwell',
    'rachel_concerned': 'Rachel',
    'rachel_friendly': 'Rachel',
    'derek_nervous': 'Derek',
    'derek_smug': 'Derek',
    'dominic': 'Dominic',
    'dominic_professional': 'Dominic',
    'dominic_intimate': 'Dominic',
    'camille': 'Camille',
    'camille_elegant': 'Camille',
};

// Choice-only tag keys: if these appear as a NARRATIVE tag (after `]` instead of inside it),
// it's a writer-side bracket bug — the choice looked clickable but the tag never gated the UI.
// See `.claude/rules/ink-narrative.md § Syntax`. Detected here so disabled-choice telemetry
// can flag the bug class instead of generic "Unknown tag" noise.
const CHOICE_ONLY_TAG_KEYS = new Set([
    'disabled', 'energy', 'time', 'fem', 'sub', 'shame',
    'hotspot', 'color', 'choiceimg', 'max', 'req'
]);

function _reportBracketBug(key, val, story) {
    let knot = '';
    try { knot = story?.state?.currentPathString || ''; } catch(e) {}
    const detail = { key, val, knot, ts: Date.now() };
    console.warn(`[BracketBug] Choice-only tag #${key}:${val} fired as narrative tag (knot: ${knot || 'unknown'}). Likely bracket placement bug — see .claude/rules/ink-narrative.md.`);
    try {
        if (!window.__bracketBugs) window.__bracketBugs = [];
        if (window.__bracketBugs.length < 200) window.__bracketBugs.push(detail);
    } catch(e) {}
    try { window.dispatchEvent(new CustomEvent('bracket-bug', { detail })); } catch(e) {}
}

class TagHandler {
    constructor() {
        /** @type {string|null} Last #id: tag value, consumed by story manager */
        this.currentLineId = null;

        /** @type {string|null} [goto:Knot] token captured off an SMS line's
         *  trailing #id tag (Ink tags run to EOL so `#id:X [goto:Y]` lands the
         *  goto inside the id value). Drives the tappable SMS CTA button. */
        this.currentLineGoto = null;

        /** @type {boolean} Set true when current line should not render as story content (e.g. SMS text) */
        this.suppressContent = false;

        // Story reference (set by story manager after init)
        this._story = null;

        /** @type {ReturnType<typeof events.createScope>} Scoped event subs; destroy() detaches all. */
        this._scope = events.createScope('tag-handler');

        // Build dispatch table: tag key -> handler function
        this._handlers = {
            [InkTags.IMG]:          (v) => this._handleImage(v),
            [InkTags.GIF]:          (v) => this._handleVideo(v),
            [InkTags.INFO]:         (v) => this._handleChar(v),
            [InkTags.CHAR]:         (v) => this._handleChar(v),
            [InkTags.UI]:           (v) => this._handleUI(v),
            [InkTags.ITEM]:         (v) => this._handleItem(v),
            [InkTags.DELAY]:        (v) => this._handleDelay(v),
            [InkTags.QUICK_TRAVEL]: (v) => this._handleQuickTravel(v),
            [InkTags.BGM]:          (v) => events.emit(Events.TAG_BGM, v),
            [InkTags.SFX]:          (v) => events.emit(Events.TAG_SFX, v),
            [InkTags.AMBIENT]:      (v) => events.emit(Events.TAG_AMBIENT, v),
            [InkTags.ACHIEVEMENT]:  (v) => events.emit(Events.TAG_ACHIEVEMENT, v),
            [InkTags.STATS]:        (v) => events.emit(Events.TAG_STATS, v),
            [InkTags.SMS]:          (v) => this._handleSMS(v),
            [InkTags.MMS]:          (v) => this._handleMMS(v),
            [InkTags.SMS_FILE]:     (v) => this._handleSMSFile(v),
            [InkTags.SMS_REPLY]:    (v) => this._handleSMSReply(v),
            [InkTags.RETURN]:       (v) => this._handleReturn(v),
            [InkTags.BREAK]:        (v) => this._handleDelay(v),
            [InkTags.ID]:           (v) => this._handleId(v),
            [InkTags.COLOR]:        (v) => events.emit(Events.TAG_COLOR, v),
            [InkTags.TUTORIAL]:     (v) => events.emit(Events.TUTORIAL_TIP, v),
            [InkTags.MONOLOGUE]:    (v) => this._handleMonologue(v),
            [InkTags.SHOT]:         (v) => events.emit(Events.TAG_SHOT, v),
            [InkTags.CHAT]:         (v) => events.emit(Events.TAG_CHAT, v),
            'location':             () => {},  // informational — handled by location maps via currentLocation
        };

        // Listen for story clear to reset ui_mode. The deferred family-clear
        // path in story.js passes { keepUIMode: true } so the just-fired
        // #ui:shop_X tag (mall pharmacy / lingerie / etc) doesn't get wiped
        // the moment the shop opens — see _clearContentView comment.
        this._scope.on(Events.STORY_CLEAR, (opts) => {
            if (opts && opts.keepUIMode) return;
            this.clearUIMode();
        });
    }

    destroy() {
        if (this._scope) {
            this._scope.destroy();
            this._scope = null;
        }
    }

    /**
     * Set the inkjs Story reference for ui_mode sync.
     * @param {object} story - inkjs Story instance
     */
    setStory(story) {
        this._story = story;
    }

    // ------------------------------------------------------------------
    // Public API
    // ------------------------------------------------------------------

    /**
     * Process an array of raw tag strings from story.currentTags.
     * @param {string[]} tags
     */
    handleTags(tags) {
        if (!tags || tags.length === 0) return;

        // Single-pass walk that respects ordering. inkjs delivers tags in
        // source order, and `#sms:body #id:Knot.sms1` compiles as adjacent
        // tokens (sms first, id second). Earlier two-pass code did a global
        // first pass that overwrote currentLineId on EVERY #id:, leaving
        // #sms: looking up the L10n key of an unrelated downstream
        // narration line. Concrete bug: Girlfriend_EarlyVisit_MasterEncounter
        // SMS body 'Nice girlfriend' rendered as the next narration's text
        // 'Sarah turns to you. He's intense.' (foundby:kaneeden v0.2.5.7.3).
        //
        // New rule: when we hit an SMS-family tag, prefer the #id: that
        // appears AFTER it (the trailing inline id on the same Ink line).
        // Fall back to currentLineId so unscoped #sms: tags still work.
        const SMS_FAMILY = new Set(['sms', 'mms', 'smsfile', 'smsreply']);
        const findTrailingId = (idx) => {
            for (let j = idx + 1; j < tags.length; j++) {
                const t = tags[j];
                if (typeof t !== 'string') continue;
                if (t.startsWith('id:')) return t.substring(3).trim();
                // Stop at next SMS-family tag — that one owns subsequent ids
                const c = t.indexOf(':');
                if (c > 0 && SMS_FAMILY.has(t.substring(0, c).trim().toLowerCase())) break;
            }
            return null;
        };

        for (let i = 0; i < tags.length; i++) {
            const raw = tags[i];
            const colonIdx = raw.indexOf(':');
            if (colonIdx < 0) continue;

            const key = raw.substring(0, colonIdx).trim().toLowerCase();
            const val = raw.substring(colonIdx + 1).trim();

            if (key === 'id') {
                this.currentLineId = val;
                continue;
            }

            // SMS-family: temporarily swap currentLineId to the trailing id
            // (if any), so the handler's L.get() lookup hits the right key.
            if (SMS_FAMILY.has(key)) {
                let trailing = findTrailingId(i);
                // The [goto:Knot] often rides on the trailing #id value because
                // Ink tags run to end-of-line (`#id:Knot.sms [goto:Scene]`).
                // Split it off so the L10n key stays clean (L.get hits) AND the
                // goto reaches the SMS handler to render the tappable CTA button.
                let trailingGoto = null;
                if (trailing) {
                    const gm = trailing.match(/\[goto:([A-Za-z0-9_]+)\]/);
                    if (gm) { trailingGoto = gm[1]; trailing = trailing.replace(gm[0], '').trim(); }
                }
                const saved = this.currentLineId;
                const savedGoto = this.currentLineGoto;
                if (trailing) this.currentLineId = trailing;
                this.currentLineGoto = trailingGoto;
                try {
                    const handler = this._handlers[key];
                    if (handler) handler(val);
                } finally {
                    this.currentLineId = saved;
                    this.currentLineGoto = savedGoto;
                }
                continue;
            }

            const handler = this._handlers[key];
            if (handler) {
                handler(val);
            } else if (CHOICE_ONLY_TAG_KEYS.has(key)) {
                _reportBracketBug(key, val, this._story);
            } else {
                console.warn(`[TagHandler] Unknown Ink tag: ${key} with value ${val}`);
            }
        }
    }

    /**
     * Parse choice-specific tags from a choice's tags array.
     * Returns an object with extracted metadata (energy, time, fem, sub, etc.).
     * @param {string[]} tags
     * @returns {object}
     */
    parseChoiceTags(tags) {
        const result = {
            energyCost: -1,
            timeCost: -1,
            arousalGain: -1,
            femGain: -1,
            subGain: -1,
            shameGain: -1,
            isDisabled: false,
            disabledReason: null,
            reqChecks: [],     // {stat, threshold, reason}
            maxChecks: [],     // {stat, threshold, reason}
            color: null,
            locId: null,
            isDramatic: false,
            choiceImg: null,   // Optional thumbnail image name (e.g. #choiceimg:master_smirk)
            hotspot: null,     // Explicit location map hotspot ID (e.g. #hotspot:bedroom)
        };

        if (!tags || tags.length === 0) return result;

        for (const raw of tags) {
            // Handle tags without values (e.g. "dramatic")
            const colonIdx = raw.indexOf(':');
            if (colonIdx < 0) {
                const trimmed = raw.trim().toLowerCase();
                if (trimmed === 'dramatic') result.isDramatic = true;
                continue;
            }

            const key = raw.substring(0, colonIdx).trim().toLowerCase();
            const fullVal = raw.substring(colonIdx + 1).trim();

            switch (key) {
                case InkTags.ENERGY: {
                    const n = parseInt(fullVal, 10);
                    if (!isNaN(n)) result.energyCost = n;
                    break;
                }
                case InkTags.TIME: {
                    const n = parseInt(fullVal, 10);
                    if (!isNaN(n)) result.timeCost = n;
                    break;
                }
                case InkTags.FEM: {
                    const n = parseInt(fullVal, 10);
                    if (!isNaN(n)) result.femGain = n;
                    break;
                }
                case InkTags.SUB: {
                    const n = parseInt(fullVal, 10);
                    if (!isNaN(n)) result.subGain = n;
                    break;
                }
                case InkTags.SHAME: {
                    const n = parseInt(fullVal, 10);
                    if (!isNaN(n)) result.shameGain = n;
                    break;
                }
                case InkTags.AROUSAL_TAG: {
                    const n = parseInt(fullVal, 10);
                    if (!isNaN(n)) result.arousalGain = n;
                    break;
                }
                case InkTags.DISABLED: {
                    // The `#disabled:` literal is ALWAYS emitted by Ink, even
                    // when its value is an inline conditional that evaluates to
                    // empty — e.g. `#disabled:{Money < 20: Need $20}` still emits
                    // a bare `disabled:` tag once Money >= 20. Gating on tag
                    // presence alone leaves such a choice permanently greyed with
                    // no reason shown. Only disable when a real reason survives.
                    // foundby: Spa Cold Plunge Stranger — $540 player could not
                    // pick "Pay for the steam room with him".
                    const reason = fullVal.replace(/^["']|["']$/g, '').trim();
                    if (reason.length > 0) {
                        result.isDisabled = true;
                        result.disabledReason = reason;
                    }
                    break;
                }
                case InkTags.REQ: {
                    // Format: req:StatName:threshold:reason
                    const parts = raw.split(':');
                    if (parts.length >= 4) {
                        result.reqChecks.push({
                            stat: parts[1].trim(),
                            threshold: parseInt(parts[2].trim(), 10),
                            reason: parts.slice(3).join(':').trim(),
                        });
                    }
                    break;
                }
                case InkTags.MAX: {
                    // Format: max:StatName:threshold:reason
                    const parts = raw.split(':');
                    if (parts.length >= 4) {
                        result.maxChecks.push({
                            stat: parts[1].trim(),
                            threshold: parseInt(parts[2].trim(), 10),
                            reason: parts.slice(3).join(':').trim(),
                        });
                    }
                    break;
                }
                case InkTags.COLOR:
                    result.color = fullVal.toLowerCase();
                    break;
                case InkTags.ID:
                    result.locId = fullVal;
                    break;
                case InkTags.CHOICEIMG:
                    result.choiceImg = fullVal;
                    break;
                case InkTags.HOTSPOT:
                    result.hotspot = fullVal;
                    break;
            }
        }

        // L10n: translated #disabled reason via <choice_id>.disabled key.
        // Extractor at tools/backfill_en_l10n.py stores the raw EN template
        // (may include `{cond:body}{not cond:body2}` + `${var}` interpolation
        // for dynamic prices). Route through L.translateLine() so EN returns
        // the Ink-resolved fullVal unchanged and non-EN gets the translated
        // template re-evaluated against live state.
        if (result.disabledReason && result.locId) {
            const key = `${result.locId}.disabled`;
            result.disabledReason = L.translateLine(key, result.disabledReason);
        }

        // L10n: #req:/#max: gate reasons via <choice_id>.req.<stat> /
        // .max.<stat> keys (extractor: tools/backfill_en_l10n.py). Mirror of the
        // #disabled path above — EN returns the Ink-resolved reason unchanged,
        // non-EN gets the translated template. Keyed per stat so a multi-gate
        // choice (Fem AND Sub) translates each line independently.
        if (result.locId) {
            for (const r of result.reqChecks) {
                if (r.reason) {
                    r.reason = L.translateLine(`${result.locId}.req.${String(r.stat).toLowerCase()}`, r.reason);
                }
            }
            for (const r of result.maxChecks) {
                if (r.reason) {
                    r.reason = L.translateLine(`${result.locId}.max.${String(r.stat).toLowerCase()}`, r.reason);
                }
            }
        }

        return result;
    }

    /**
     * Resolve a #char: portrait value to a human-readable speaker name.
     * @param {string} portrait
     * @returns {string}
     */
    resolveSpeakerName(portrait) {
        if (!portrait || portrait === 'info') return 'info';

        // Check compound name map first
        if (COMPOUND_SPEAKER_NAMES[portrait]) {
            return COMPOUND_SPEAKER_NAMES[portrait];
        }

        // Check constants display names
        if (SPEAKER_DISPLAY_NAMES[portrait] !== undefined) {
            return SPEAKER_DISPLAY_NAMES[portrait]; // may be null for dynamic names
        }

        // Default: capitalize first segment before underscore
        const first = portrait.split('_')[0];
        return first.charAt(0).toUpperCase() + first.slice(1);
    }

    /**
     * Clear the stored line ID after the story manager reads it.
     */
    clearLineId() {
        this.currentLineId = null;
    }

    /**
     * Sync ui_mode to Ink variable. Called by #ui: handler.
     * @param {string} value
     */
    syncUIMode(value) {
        if (!this._story) return;
        try {
            this._story.variablesState['ui_mode'] = value || '';
        } catch (e) {
            // ui_mode var may not exist in compiled story
        }
    }

    /**
     * Clear the persisted ui_mode in Ink. Called on STORY_CLEAR.
     */
    clearUIMode() {
        if (!this._story) return;
        try {
            this._story.variablesState['ui_mode'] = '';
        } catch (e) { /* ignore */ }
        // Notify all UI listeners so overlays (shop, etc.) can close
        events.emit(Events.TAG_UI, '');
    }

    /**
     * Re-fire #ui: tag from saved ui_mode to restore UI state after load.
     * @param {object} story - inkjs Story instance
     */
    restoreUIMode(story) {
        if (!story) return;
        try {
            const mode = story.variablesState['ui_mode'];
            if (mode && mode !== '') {
                events.emit(Events.TAG_UI, mode);
            }
        } catch (e) { /* ignore */ }
    }

    // ------------------------------------------------------------------
    // Private handlers
    // ------------------------------------------------------------------

    _handleImage(value) {
        events.emit(Events.TAG_IMAGE, value);
    }

    _handleVideo(value) {
        events.emit(Events.TAG_VIDEO, value);
    }

    _handleChar(value) {
        events.emit(Events.TAG_CHAR, value);
    }

    _handleUI(value) {
        this.syncUIMode(value);
        events.emit(Events.TAG_UI, value);
    }

    _handleItem(value) {
        // Parse action prefix: equip_, add_, unequip_, save_outfit, restore_outfit, sync_flags
        if (value === 'save_outfit') {
            events.emit(Events.TAG_ITEM, 'save_outfit');
        } else if (value === 'restore_outfit') {
            events.emit(Events.TAG_ITEM, 'restore_outfit');
        } else if (value === 'sync_flags') {
            // Forces _recalculateAllFlags() to push EquippedFeminine / wearing_*
            // back to Ink. Recovers from save desync on job-hub entry.
            events.emit(Events.TAG_ITEM, 'sync_flags');
        } else if (value === 'recalc_buffs') {
            // Forces recalculateAllBuffs() — used by training scenes after
            // a *_tolerance VAR flip so the new buff array kicks in mid-cycle.
            events.emit(Events.TAG_ITEM, 'recalc_buffs');
        } else if (value === 'retire_masc_clothes') {
            // Overnight "can't present masculine past Fem 50" beat — strips any
            // masc top/bottom and re-dresses feminine (inventory.js).
            events.emit(Events.TAG_ITEM, 'retire_masc');
        } else if (value === 'destroy_front') {
            // Locksmith cuts a welded cage apart: unequip + remove ownership
            // (inventory.js recomputes owns_chastity from the remaining set).
            events.emit(Events.TAG_ITEM, 'destroy_front');
        } else if (value.startsWith('equip_')) {
            events.emit(Events.TAG_ITEM, 'equip', value.substring(6));
        } else if (value.startsWith('add_')) {
            events.emit(Events.TAG_ITEM, 'add', value.substring(4));
        } else if (value.startsWith('unequip_')) {
            events.emit(Events.TAG_ITEM, 'unequip', value.substring(8));
        } else {
            console.warn(`[TagHandler] Unknown item tag action: ${value}`);
        }
    }

    _handleDelay(value) {
        const seconds = parseFloat(value);
        if (!isNaN(seconds)) {
            events.emit(Events.TAG_BREAK, seconds);
        }
    }

    _handleQuickTravel(value) {
        // Emit as a generic variable change; frontend handles it
        events.emit(Events.VARIABLE_CHANGED, 'quickTravelAllowed', value === 'true');
    }

    _handleSMS(value) {
        // value = "npcId:message text"
        const colonIdx = value.indexOf(':');
        if (colonIdx < 0) return;
        const npcId = value.substring(0, colonIdx).trim();
        let text = value.substring(colonIdx + 1).trim();
        // L10n: if this line has an #id: tag, try to translate the SMS text
        if (this.currentLineId) {
            const translated = L.get(this.currentLineId);
            if (translated !== this.currentLineId) {
                text = L.interpolate(translated);
            }
        }
        // [goto:Knot] CTA source. Primary: the trailing #id tag, captured in
        // handleTags as currentLineGoto (Ink folds `#id:X [goto:Y]` into the id
        // value). Fallback: a goto embedded directly in the #sms text. phone.js
        // gates the tappable CTA button on msg.goto — without it the player sees
        // the SMS but cannot trigger the scene it points to. The 60-day
        // permanent-chastity SMS shipped unpressable for exactly this reason.
        // foundby player (permanent-lock scene could not be triggered at day 60).
        let goto = this.currentLineGoto || null;
        const gm = text.match(/\[goto:([A-Za-z0-9_]+)\]/);
        if (gm) {
            if (!goto) goto = gm[1];
            text = text.replace(gm[0], '').trim();
        }
        this.suppressContent = true;
        events.emit(Events.TAG_SMS, { type: 'sms', npcId, text, image: null, goto });
    }

    _handleMMS(value) {
        // value = "npcId:imageName" or "npcId:imageName:caption"
        const firstColon = value.indexOf(':');
        if (firstColon < 0) return;
        const npcId = value.substring(0, firstColon).trim();
        const rest = value.substring(firstColon + 1);
        const secondColon = rest.indexOf(':');
        let image, caption;
        if (secondColon >= 0) {
            image = rest.substring(0, secondColon).trim();
            caption = rest.substring(secondColon + 1).trim() || null;
        } else {
            image = rest.trim();
            caption = null;
        }
        // L10n: look up translated caption via mms.<image> key (extracted by
        // backfill_en_l10n.py from the Ink #mms: tag payload).
        if (image && caption) {
            const key = `mms.${image}`;
            const translated = L.get(key);
            if (translated && translated !== key) caption = translated;
        }
        this.suppressContent = true;
        events.emit(Events.TAG_SMS, { type: 'mms', npcId, text: caption, image });
    }

    _handleSMSFile(value) {
        // value = "npcId:imageName:label" or "npcId:imageName:label:caption"
        // imageName resolves through window._pno_resolveImage so the file can
        // live in any subfolder (UI, Computer, SMS, Apartment, etc.).
        // label = displayed filename (e.g. "hypno_session.mp3"). Required —
        // it is the affordance that tells the player this is a tappable file.
        // caption = optional message text under the file row. May embed a
        // [goto:KnotName] token (same idiom as #smsreply) — when present,
        // tapping the row closes the phone and runs the knot as a forced
        // event instead of opening the fullscreen image. Use this when the
        // SMS attachment is a teaser for a real scene (Sasha hypno session,
        // unknown-number teasers that lead to specific Mall/Computer scenes).
        const parts = value.split(':');
        if (parts.length < 3) return;
        const npcId = parts[0].trim();
        const image = parts[1].trim();
        const label = parts[2].trim();
        let caption = parts.length > 3 ? parts.slice(3).join(':').trim() : null;
        if (!npcId || !image || !label) return;
        let goto = null;
        if (caption) {
            const m = caption.match(/\[goto:([A-Za-z0-9_]+)\]/);
            if (m) {
                goto = m[1];
                caption = caption.replace(m[0], '').trim() || null;
            }
        }
        // L10n: translate caption via smsfile.<image>.caption key.
        // Label (e.g. "sasha_special.mp3") stays untranslated by design — it's
        // a filename, not narrative text.
        if (image && caption) {
            const key = `smsfile.${image}.caption`;
            const translated = L.get(key);
            if (translated && translated !== key) caption = translated;
        }
        this.suppressContent = true;
        events.emit(Events.TAG_SMS, { type: 'smsfile', npcId, text: caption, image, label, goto });
    }

    _handleSMSReply(value) {
        // value = "npcId:[OPENING_MSG;;]BTN>>REPLY[goto:KNOT];;BTN2>>REPLY2"
        // Two formats are common:
        //   With opening message:  master:I left something for you.;;Yes Master>>...
        //   Pure reply options:    master:Yes Viktor>>[goto:K1];;No>>...
        const colonIdx = value.indexOf(':');
        if (colonIdx < 0) return;
        const npcId = value.substring(0, colonIdx).trim();
        const options = value.substring(colonIdx + 1).trim();
        const parentId = this.currentLineId || '';

        // L10n: re-emit each segment with translated text. Anchor priority:
        //   1. [goto:KNOT] in reply  →  smsreply.<KNOT>.btn|.reply (stable, semantic)
        //   2. parent #id: + opt_idx →  smsreply.<parent>.<idx>.btn|.reply
        //   Opening message (no >>) →  smsreply.<parent>.message
        const translated = options.split(';;').map((opt, idx) => {
            opt = opt.trim();
            if (!opt) return opt;
            const sepIdx = opt.indexOf('>>');
            if (sepIdx < 0) {
                // Opening NPC message segment
                if (idx === 0 && parentId) {
                    const key = `smsreply.${parentId}.message`;
                    const t = L.get(key);
                    if (t && t !== key) return t;
                }
                return opt;
            }
            let btn = opt.substring(0, sepIdx).trim();
            let reply = opt.substring(sepIdx + 2).trim();
            const gotoMatch = reply.match(/\[goto:([A-Za-z0-9_]+)\]/);
            let anchor;
            if (gotoMatch) {
                // idx-based disambiguation when multiple replies share the
                // same goto knot (common: Yes/Busy options both routing to
                // the same forced scene). idx=0 keeps the canonical
                // `smsreply.<KNOT>.btn` shape; idx>=1 gets `.idx` suffix.
                // Without this, both options overwrote the same L10n key
                // and tags.js returned the LATER option's text for both.
                anchor = idx === 0 ? gotoMatch[1] : `${gotoMatch[1]}.${idx}`;
            } else if (parentId) {
                anchor = `${parentId}.${idx}`;
            }
            if (anchor) {
                const btnKey = `smsreply.${anchor}.btn`;
                const replyKey = `smsreply.${anchor}.reply`;
                const tBtn = L.get(btnKey);
                if (tBtn && tBtn !== btnKey) btn = tBtn;
                const tReply = L.get(replyKey);
                if (tReply && tReply !== replyKey) {
                    // Preserve [goto:X] / [img:Y] tokens that the translator's
                    // clean text doesn't carry.
                    const tokens = (reply.match(/\[(?:goto|img):[^\]]+\]/g) || []).join('');
                    reply = tReply + (tokens ? ' ' + tokens : '');
                }
            }
            return `${btn}>>${reply}`;
        }).join(';;');
        events.emit(Events.TAG_SMS_REPLY, { npcId, options: translated });
    }

    _handleReturn(value) {
        events.emit(Events.TAG_RETURN, value);
    }

    _handleId(value) {
        this.currentLineId = value;
        // Reset per-parent counters (monologue stacking) when entering a new line
        this._monologueCount = 0;
    }

    _handleMonologue(text) {
        const parentId = this.currentLineId;
        if (parentId) {
            this._monologueCount = (this._monologueCount || 0) + 1;
            const n = this._monologueCount;
            const key = n === 1 ? `${parentId}.monologue` : `${parentId}.monologue.${n}`;
            const t = L.get(key);
            if (t && t !== key) text = t;
        }
        events.emit(Events.TAG_MONOLOGUE, text);
    }
}

/** @type {TagHandler} Singleton */
export const tagHandler = new TagHandler();
